/**
 * What the Memory page shows (slice 1d spec §3, §6): the files by section,
 * what each is and when it loads, the counter against its limit, who edited
 * it last, and the editor's state as saves, conflicts and live changes meet
 * the owner's draft. Pure, so it's tested on its own (`memory-view.test.ts`).
 */
import { draftKit, type Draft } from "../../app/draft";
import { dayBefore, localDate } from "../../../worker/domain/routine";
import type { Me } from "../../app/me";
import type { Budget, MemoryFile, MemoryFileMeta, MemoryNote, UpdatedBy } from "./api";

/** OpenClaw's `bootstrapMaxChars`: each file's cap (§3.4). */
const FILE_LIMIT = 20_000;
/** OpenClaw's `bootstrapTotalMaxChars`: every file loaded at the start of a run, together. */
const EVERY_RUN_LIMIT = 60_000;

type Agent = Me["agents"][number];

export type FileKind = {
  name: string;
  /** The page's name for it. */
  title: string;
  scope: "workspace" | "agent";
  /** Injected at the start of every run, or read when the work calls for it (§3.1). */
  everyRun: boolean;
  /** What it is, for the given agent (per-agent files) or the workspace. */
  about: (agent: string) => string;
  /** When it loads, in the editor's subtitle. */
  loads: string;
};

const EVERY_RUN = "It loads at the start of every run.";

/** §3.1's files, in the page's order. */
const FILE_KINDS: FileKind[] = [
  {
    name: "USER.md",
    title: "Context",
    scope: "workspace",
    everyRun: true,
    about: () => "What the workspace is and does, its people, and the facts that stand.",
    loads: EVERY_RUN,
  },
  {
    name: "VOICE.md",
    title: "Voice",
    scope: "workspace",
    everyRun: false,
    about: () => "How the workspace sounds: tone, words to use and avoid, the sign-off.",
    loads: "It's read when writing anything sent out, like an email or a post.",
  },
  {
    name: "CREW.md",
    title: "Crew",
    scope: "workspace",
    everyRun: false,
    about: () => "Who's on the crew and what each one does.",
    loads: "It's read when the work involves someone else on the crew.",
  },
  {
    name: "MEMORY.md",
    title: "Notes",
    scope: "agent",
    everyRun: true,
    about: (agent) =>
      `What ${agent} has learned and keeps: preferences, decisions, standing facts.`,
    loads: `${EVERY_RUN} It keeps them itself.`,
  },
  {
    name: "SOUL.md",
    title: "Personality",
    scope: "agent",
    everyRun: true,
    about: (agent) => `How ${agent} sounds and what it values.`,
    loads: EVERY_RUN,
  },
  {
    name: "IDENTITY.md",
    title: "Identity",
    scope: "agent",
    everyRun: true,
    about: (agent) => `${capital(agent)}'s name, role and emoji.`,
    loads: EVERY_RUN,
  },
];

/**
 * Each agent's daily notes (slice 1m spec §7): not a file but a page a day,
 * listed after its Notes. `?agent=main&file=daily-notes`.
 */
const DAILY_NOTES: FileKind = {
  name: "daily-notes",
  title: "Daily notes",
  scope: "agent",
  everyRun: false,
  about: (agent) => `What ${agent} noted as it worked, one page a day.`,
  loads: "Today's and yesterday's reach every chat; it searches older days when it needs them.",
};

export const isDailyNotes = (entry: Pick<Entry, "kind"> | undefined) =>
  entry?.kind.name === DAILY_NOTES.name;

/** An agent's entries: its files, with Daily notes after Notes. */
const AGENT_KINDS = FILE_KINDS.filter((k) => k.scope === "agent").flatMap((k) =>
  k.name === "MEMORY.md" ? [k, DAILY_NOTES] : [k],
);

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** How the page names an agent in a sentence: "the Chief of Staff", or a specialist's own name. */
export function agentPhrase(agent: Pick<Agent, "slug" | "name">): string {
  return agent.slug === "main" ? `the ${agent.name}` : agent.name;
}

/** Whether the agent keeps daily notes in Crew: a pi computer's does (slice 1m §7). */
export const keepsDailyNotes = (me: Pick<Me, "computer">) => me.computer?.daily_notes === true;

/** One file on the page: its kind, its agent (per-agent files), and its metadata once listed. */
export type Entry = {
  key: string;
  kind: FileKind;
  agent: Agent | null;
  meta: MemoryFileMeta | undefined;
  href: string;
};

export type Sections = {
  workspace: Entry[];
  agents: Array<{ agent: Agent; entries: Entry[] }>;
};

/** Where a file is on the page: `?file=USER.md`, or `?agent=main&file=MEMORY.md`. */
function memoryHref(name: string, agentSlug?: string | null): string {
  const q = new URLSearchParams();
  if (agentSlug) q.set("agent", agentSlug);
  q.set("file", name);
  return `/memory?${q.toString()}`;
}

/** A file by name, for a page outside Memory: its title and its link (an agent's file is that agent's). */
export function fileLink(name: string, agentSlug: string | null): { title: string; href: string } {
  const kind = FILE_KINDS.find((k) => k.name === name);
  return {
    title: kind?.title ?? name,
    href: memoryHref(name, kind?.scope === "agent" ? agentSlug : null),
  };
}

/** A file's link from the page itself: the side panel's `?panel=` stays. */
export function withFile(search: string, entry: Entry): string {
  const next = new URLSearchParams(entry.href.slice(entry.href.indexOf("?")));
  const panel = new URLSearchParams(search).get("panel");
  if (panel) next.set("panel", panel);
  return `/memory?${next.toString()}`;
}

/**
 * The Workspace section, then one per agent (the Chief of Staff first), each
 * with its files in §3.1's order; files the API lists under other names are
 * left out. Daily notes only where the agent keeps them in Crew: on pi (slice
 * 1m §7); OpenClaw's stay on its computer.
 */
export function memorySections(
  files: MemoryFileMeta[],
  agents: Agent[],
  { dailyNotes = true }: { dailyNotes?: boolean } = {},
): Sections {
  const find = (name: string, agentId: string | null) =>
    files.find((f) => f.name === name && f.agent_id === agentId);
  const entry = (kind: FileKind, agent: Agent | null): Entry => ({
    key: agent ? `${agent.slug}/${kind.name}` : kind.name,
    kind,
    agent,
    meta: find(kind.name, agent?.id ?? null),
    href: memoryHref(kind.name, agent?.slug),
  });
  const ordered = [...agents].sort((a, b) => Number(b.slug === "main") - Number(a.slug === "main"));
  return {
    workspace: FILE_KINDS.filter((k) => k.scope === "workspace").map((k) => entry(k, null)),
    agents: ordered.map((agent) => ({
      agent,
      entries: AGENT_KINDS.filter((k) => dailyNotes || k !== DAILY_NOTES).map((k) =>
        entry(k, agent),
      ),
    })),
  };
}

/** The file the URL names, else the Chief of Staff's Notes, else the first there is. */
export function pickEntry(sections: Sections, params: URLSearchParams): Entry | undefined {
  const all = [...sections.workspace, ...sections.agents.flatMap((a) => a.entries)];
  const file = params.get("file");
  const agent = params.get("agent");
  const named = all.find(
    (e) =>
      e.kind.name === file && (e.agent?.slug ?? null) === (e.kind.scope === "agent" ? agent : null),
  );
  return named ?? sections.agents[0]?.entries[0] ?? all[0];
}

/** The title and subtitle over the editor: what it is, and when it loads. */
export function entryHeading(entry: Entry): { title: string; about: string } {
  const who = entry.agent ? agentPhrase(entry.agent) : "your crew";
  return { title: entry.kind.title, about: `${entry.kind.about(who)} ${entry.kind.loads}` };
}

const count = new Intl.NumberFormat("en-US");

export type Counter = {
  /** "13,238 / 20,000" */
  text: string;
  /** The bar's fill, 0 to 100. */
  percent: number;
  tone: "muted" | "copper" | "brick";
  /** How far past the limit, 0 when within it. */
  over: number;
};

/** The counter and bar: copper from 90%, brick past the limit. */
export function counter(length: number, limit = FILE_LIMIT): Counter {
  const ratio = length / limit;
  return {
    text: `${count.format(length)} / ${count.format(limit)}`,
    percent: Math.min(100, Math.round(ratio * 1000) / 10),
    tone: ratio > 1 ? "brick" : ratio >= 0.9 ? "copper" : "muted",
    over: Math.max(0, length - limit),
  };
}

/** Why the draft can't be saved as it is, or null. */
export function overLimit(length: number, limit = FILE_LIMIT): string | null {
  const over = length - limit;
  return over > 0
    ? `${count.format(over)} ${over === 1 ? "character" : "characters"} over the ${count.format(limit)} limit. Shorten it to save.`
    : null;
}

/**
 * The every-run total, from a save refused `over_budget` (§3.4): the
 * characters left across the files loaded at the start of every run.
 */
export function budgetNote(left: number | null): string {
  const total = count.format(EVERY_RUN_LIMIT);
  if (left === null) return `The files that load every run are over their ${total} total.`;
  const n = Math.max(0, left);
  return `The files that load every run are near their ${total} total: ${count.format(n)} ${n === 1 ? "character" : "characters"} left.`;
}

/**
 * The every-run total, up front (`GET /api/memory`'s `budget`), for a file
 * that loads every run: what would be left with this draft saved. Null for
 * on-demand files, or before the API sends a budget.
 */
export function everyRunLeft(
  budget: Budget | undefined,
  everyRun: boolean,
  draftLength: number,
  savedLength: number,
): { text: string; tone: Counter["tone"] } | null {
  if (!budget || !everyRun) return null;
  const left = budget.every_run_left - (draftLength - savedLength);
  const max = count.format(budget.every_run_max);
  if (left < 0) {
    return { text: `${count.format(-left)} over the ${max} every-run total`, tone: "brick" };
  }
  return {
    text: `${count.format(left)} of ${max} left for every run`,
    tone: left < budget.every_run_max * 0.1 ? "copper" : "muted",
  };
}

/** Who `updated_by` names, as a sentence has it: "the Chief of Staff", "Nadia Bell". */
export function actorName(updatedBy: UpdatedBy, me: Pick<Me, "user" | "agents">): string {
  const [kind, id] = splitActor(updatedBy);
  if (kind === "agent") {
    const agent = me.agents.find((a) => a.id === id);
    return agent ? agentPhrase(agent) : "an agent";
  }
  return id === me.user.id ? me.user.name : "someone in the workspace";
}

function splitActor(updatedBy: string): ["agent" | "person", string] {
  const at = updatedBy.indexOf(":");
  const kind = updatedBy.slice(0, at);
  return [kind === "agent" ? "agent" : "person", updatedBy.slice(at + 1)];
}

/** "just now", "5 minutes ago", "2 hours ago", "yesterday", "3 days ago", then "Sep 3". */
export function relativeTime(iso: string, timeZone: string, now = Date.now()): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return "";
  const minutes = Math.floor(Math.max(0, now - at) / 60_000);
  const ago = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"} ago`;
  if (minutes < 1) return "just now";
  if (minutes < 60) return ago(minutes, "minute");
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return ago(hours, "hour");
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return ago(days, "day");
  return new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric" }).format(at);
}

/** "Edited by the Chief of Staff · 2 hours ago". */
export function editedBy(
  meta: Pick<MemoryFileMeta, "updated_by" | "updated_at">,
  me: Pick<Me, "user" | "agents" | "workspace">,
  now = Date.now(),
): string {
  const when = relativeTime(meta.updated_at, me.workspace.timezone, now);
  return `Edited by ${actorName(meta.updated_by, me)}${when ? ` · ${when}` : ""}`;
}

/** "The Chief of Staff changed this file.", over a draft a live change passed. */
export function changedNotice(file: MemoryFile, me: Pick<Me, "user" | "agents">): string {
  return `${capital(actorName(file.updated_by, me))} changed this file.`;
}

/**
 * The editor (`app/draft.ts`): the version the draft started from (`base`),
 * the draft, the newer version a save ran into (`conflict`), and a newer
 * version that arrived live while the draft had changes (`newer`).
 */
export type Editor = Draft<MemoryFile, string>;

const kit = draftKit<MemoryFile, string>(
  (file) => file.content,
  (a, b) => a === b,
);

export const {
  editorFor,
  isDirty,
  edit,
  discard,
  receive,
  saved,
  undone,
  reload,
  conflicted,
  keepMine,
  takeTheirs,
} = kit;

/** Every file's editor the owner has opened, by file id: a draft outlives a switch to another file. */
export type Drafts = Record<string, Editor>;

/** The files with unsaved changes, for the list's dots. */
export function unsavedIds(drafts: Drafts): Set<string> {
  return new Set(Object.keys(drafts).filter((id) => isDirty(drafts[id])));
}

/** Save only with a change within the limit, and not while a conflict waits for a choice. */
export function canSave(e: Editor, limit = FILE_LIMIT): boolean {
  return isDirty(e) && e.draft.length <= limit && !e.conflict;
}

const NOTE_WORDS: Record<string, string> = { "MEMORY.md": "notes", "USER.md": "context" };

/**
 * The quiet line under a reply whose turn changed the agent's notes or the
 * workspace's context (§6.2): "Updated its notes", linking to the file.
 */
export function memoryNote(
  updatedFiles: string[] | undefined,
  agentSlug: string | null,
): { text: string; href: string } | null {
  const names = Object.keys(NOTE_WORDS).filter((n) => updatedFiles?.includes(n));
  if (names.length === 0) return null;
  const first = names[0];
  return {
    text: `Updated its ${names.map((n) => NOTE_WORDS[n]).join(" and ")}`,
    href: memoryHref(first, first === "MEMORY.md" ? agentSlug : null),
  };
}

/** A day's note's cap (slice 1m spec §2). */
export const NOTE_LIMIT = 8_000;

/** `YYYY-MM-DD` in the workspace's timezone. */
const dayIn = (timeZone: string, at: number) => localDate(new Date(at), timeZone);

/** "Tue 14 Oct", with the year when it isn't this one: "Tue 14 Oct 2025". */
function dayDate(day: string, timeZone: string, now = Date.now()): string {
  const at = new Date(`${day}T12:00:00Z`);
  const sameYear = day.slice(0, 4) === dayIn(timeZone, now).slice(0, 4);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  }).formatToParts(at);
  const of = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return [of("weekday"), of("day"), of("month"), sameYear ? "" : of("year")]
    .filter(Boolean)
    .join(" ");
}

/** A day's heading: "Today" or "Yesterday" with its date beside it, else the date alone. */
export function dayHeading(
  day: string,
  timeZone: string,
  now = Date.now(),
): { title: string; date: string | null } {
  const today = dayIn(timeZone, now);
  const date = dayDate(day, timeZone, now);
  if (day === today) return { title: "Today", date };
  if (day === dayBefore(today)) return { title: "Yesterday", date };
  return { title: date, date: null };
}

/** One line of a day: its time (`14:05`) when the agent noted it, else a bullet's text. */
export type NoteLine = { time: string | null; text: string };

const TIMED = /^[-*]\s+(\d{1,2}:\d{2})\s+(.+)$/;
const BULLET = /^[-*]\s+(.+)$/;

/**
 * A day's lines, when every line is a note (`- 14:05 …`) or a bullet; null
 * otherwise (headings, paragraphs: an imported day, or one the owner wrote),
 * which shows as markdown.
 */
export function noteLines(content: string): NoteLine[] | null {
  const lines = content.split("\n").filter((l) => l.trim() !== "");
  if (lines.length === 0) return null;
  const parsed: NoteLine[] = [];
  for (const line of lines) {
    const t = line.trim();
    const timed = TIMED.exec(t);
    if (timed) {
      parsed.push({ time: timed[1].padStart(5, "0"), text: timed[2] });
      continue;
    }
    const bullet = BULLET.exec(t);
    if (!bullet) return null;
    parsed.push({ time: null, text: bullet[1] });
  }
  return parsed;
}

/** Who a note's `updated_by` names: an agent by id or slug, a person, or the import. */
function noteActor(updatedBy: string, me: Pick<Me, "user" | "agents">): string {
  const at = updatedBy.indexOf(":");
  const kind = at < 0 ? updatedBy : updatedBy.slice(0, at);
  const id = updatedBy.slice(at + 1);
  if (kind === "agent") {
    const agent = me.agents.find((a) => a.id === id || a.slug === id);
    return agent ? agentPhrase(agent) : "an agent";
  }
  return id === me.user.id ? me.user.name : "someone in the workspace";
}

/**
 * Under a day: "Noted by the Chief of Staff · 2 hours ago", "Edited by Nadia
 * Bell · yesterday", or "From the old computer · Oct 21" for an imported day.
 */
export function notedBy(
  note: Pick<MemoryNote, "updated_by" | "updated_at">,
  me: Pick<Me, "user" | "agents" | "workspace">,
  now = Date.now(),
): string {
  const when = relativeTime(note.updated_at, me.workspace.timezone, now);
  const tail = when ? ` · ${when}` : "";
  if (note.updated_by === "import") return `From the old computer${tail}`;
  const verb = note.updated_by.startsWith("agent:") ? "Noted" : "Edited";
  return `${verb} by ${noteActor(note.updated_by, me)}${tail}`;
}

/** A conflicting save's author, as a sentence starts: "The Chief of Staff", "The old computer's import". */
export function noteAuthor(updatedBy: string, me: Pick<Me, "user" | "agents">): string {
  if (updatedBy === "import") return "The move from the old computer";
  return capital(noteActor(updatedBy, me));
}

/** A day's editor (`app/draft.ts`), like a file's. */
export type NoteEditor = Draft<MemoryNote, string>;

export const noteKit = draftKit<MemoryNote, string>(
  (note) => note.content,
  (a, b) => a === b,
);

/** Save a day with a change within its cap, and not while a conflict waits. */
export function canSaveNote(e: NoteEditor): boolean {
  return noteKit.isDirty(e) && e.draft.length <= NOTE_LIMIT && !e.conflict;
}
